import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import JiraDot from './jiraDot';
import { useUser } from '../../context/UserContext';
import { usePermissions } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../constants/permissions';
import { LayoutDashboard, FolderKanban, Settings, Shield, ChevronDown, ChevronLeft, ChevronRight, ClipboardList, Users, KeyRound, UsersRound } from 'lucide-react';

export default function Sidebar({ mobileDrawerOpen = false, onMobileDrawerClose, onShowAdminPanel, onShowAccountOverlay, onShowApiTokens, collapsed = false, onToggleCollapse }) {

  const [settingsOpen, setSettingsOpen] = useState(false);

  const location = useLocation();
  const { activeJiraAccount, jiraAccountStatus } = useUser();
  const { can } = usePermissions();

  const jiraDotProps = useMemo(() => {
    const needsAction =
      !jiraAccountStatus.hasAccounts ||
      jiraAccountStatus.hasAccountsButNoneActive ||
      (activeJiraAccount && jiraAccountStatus.activeAccountConnectionFailed);
    const hasButNoneActive = jiraAccountStatus.hasAccountsButNoneActive;
    const hasActive = !!activeJiraAccount;
    const dotColor =
      hasButNoneActive || (hasActive && needsAction)
        ? 'bg-orange-500'
        : !hasActive && needsAction
          ? 'bg-rose-500'
          : 'bg-emerald-400';
    return { dotColor, showPulse: needsAction };
  }, [activeJiraAccount, jiraAccountStatus]);

  const navItems = [
    { name: 'Dashboard', path: '/', icon: LayoutDashboard },
    { name: 'Projects', path: '/projects', icon: FolderKanban },
    { name: 'Team', path: '/team', icon: UsersRound },
    { name: 'Capacity Overview', path: '/projects/capacity', icon: Users },
    { name: 'Plan Overview', path: '/projects/plan', icon: ClipboardList },

  ];

  const settingsItems = [
    { name: 'Jira Accounts', key: 'jira-accounts', onClick: onShowAccountOverlay, showJiraDot: true, icon: Settings },
    { name: 'API Tokens', key: 'api-tokens', onClick: onShowApiTokens, showJiraDot: false, icon: KeyRound },
    { name: 'Admin Panel', key: 'admin', onClick: onShowAdminPanel, showJiraDot: false, icon: Shield },
  ].filter((item) => item.key !== 'admin' || can(PERMISSIONS.ADMIN_PANEL));

  const renderContent = () => (
    <div className="flex flex-col flex-1 min-h-0 h-full">
      {/* Header */}
      <div className="flex h-16 shrink-0 items-center border-b border-slate-200 dark:border-slate-700 px-3">
        {collapsed ? (
          <span className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white mx-auto">L</span>
        ) : (
          <span className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white px-3 transition-colors">
            Logio
          </span>
        )}
      </div>

      <nav className="flex-1 min-h-0 space-y-1 px-3 py-4 overflow-y-auto">
        {/* Navigation items */}
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => onMobileDrawerClose?.()}
              aria-current={isActive ? 'page' : undefined}
              title={collapsed ? item.name : undefined}
              className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${collapsed ? 'justify-center' : ''
                } ${isActive
                  ? 'bg-slate-100 text-slate-900 dark:bg-slate-700 dark:text-white'
                  : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5 hover:text-slate-900 dark:hover:text-white'
                }`}
            >
              <Icon size={18} className="shrink-0" />
              {!collapsed && item.name}
            </Link>
          );
        })}

        {/* Settings */}
        <div className="mb-2 mt-4">
          <button
            type="button"
            onClick={() => collapsed ? null : setSettingsOpen(!settingsOpen)}
            title={collapsed ? 'Settings' : undefined}
            className={`w-full px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-2 hover:text-slate-700 dark:hover:text-slate-300 transition-colors ${collapsed ? 'justify-center' : ''
              }`}
          >
            <Settings size={14} className="shrink-0" />
            {!collapsed && (
              <>
                Settings
                <ChevronDown
                  size={14}
                  className={`shrink-0 ml-auto transition-transform duration-200 ${settingsOpen ? 'rotate-180' : ''}`}
                />
              </>
            )}
          </button>
          {settingsOpen && !collapsed && (
            <div className="mt-1 space-y-0.5 ml-3">
              {settingsItems.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => {
                    item.onClick?.();
                    onMobileDrawerClose?.();
                  }}
                  className="w-full flex items-center gap-2 rounded-md py-1.5 pl-3 pr-2 text-xs font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 hover:text-slate-700 dark:hover:text-slate-200 transition-colors text-left"
                >
                  <span className="flex-1 truncate text-left">{item.name}</span>
                  {item.showJiraDot && (
                    <span className="shrink-0 ml-auto">
                      <JiraDot dotColor={jiraDotProps.dotColor} showPulse={jiraDotProps.showPulse} size="sm" />
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Collapse toggle at bottom */}
      <div className="shrink-0 border-t border-slate-200 dark:border-slate-700 p-3">
        <button
          type="button"
          onClick={onToggleCollapse}
          className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 hover:text-slate-900 dark:hover:text-white transition-colors w-full ${collapsed ? 'justify-center' : ''
            }`}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          {/* {!collapsed && 'Collapse'} */}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop: collapsible sidebar */}
      <aside
        className={`hidden md:flex shrink-0 h-screen bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 transition-all duration-300 flex-col overflow-hidden ${collapsed ? 'w-16' : 'w-64'
          }`}
      >
        {renderContent()}
      </aside>

      {/* Mobile: always full-width drawer, no collapse */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity duration-300 ${mobileDrawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        onClick={onMobileDrawerClose}
        aria-hidden="true"
      />
      <aside
        id="mobile-sidebar"
        role="dialog"
        aria-label="Mobile sidebar"
        className={`fixed inset-y-0 left-0 w-64 z-50 bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700 transition-all duration-300 ease-out md:hidden flex flex-col overflow-hidden ${mobileDrawerOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        aria-hidden={!mobileDrawerOpen}
      >
        {renderContent()}
      </aside>
    </>
  );
}